/**
 * `useChannelActivation` — is the channel's STORED credential the one the
 * runtime is actually RUNNING?
 *
 * `useChannelEndpoints` answers whether a door is LIVE. This answers the
 * narrower question that separates "nothing set up" from "saved, not active
 * yet": `GET /channels/:id/details` reports `requiresRoll`, true when the
 * config the gateway process runs does not carry the stored token
 * (`ai-docs/telegram-requiresroll-loaded-config-miniplan.md`, `dcba6266e`).
 *
 * ## The contract is THREE-state, and the third state is the whole point
 *
 *   `true`  — stored but not running: the token is saved and the bot is deaf.
 *   `false` — running: whatever else may be wrong, it is not activation.
 *   `null`  — UNKNOWN: unread, failed, or a runtime whose `/details` OMITS the
 *             field (it is optional, `lx-openclaw-config.ts:1002`).
 *
 * **`null` must never collapse onto `false`.** An image predating the bake
 * omits the field, and the SPA cannot detect which fleet it is talking to, so
 * an unknown answer has to render no claim at all rather than a convenient one.
 *
 * Telegram-only by construction: `requiresRoll` exists on the Telegram details
 * response and on no other channel's, so WhatsApp stays `null` forever here.
 */
import { useCallback, useEffect, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { getChannelDetails } from "@/api/lx-openclaw-config";

export interface ChannelActivationState {
  /** `null` = unknown — see the docblock; never rendered as either answer. */
  requiresRoll: boolean | null;
  /** Force an immediate re-read. NOT needed merely to re-enable: `read` closes
   *  over `enabled`, and the mount effect keys on `read`, so flipping `enabled`
   *  back to true re-reads on its own (which is why `ChannelPane` does not call
   *  this when its connect dialog closes). For callers with no such flip. */
  refresh: () => void;
}

export function useChannelActivation(
  channel: "telegram" | "whatsapp",
  opts?: { enabled?: boolean; pollMs?: number },
): ChannelActivationState {
  const enabled = opts?.enabled ?? true;
  const pollMs = opts?.pollMs ?? 10_000;
  const connectionState = useGatewayStore((s) => s.connectionState);
  const connected = connectionState === "connected";
  const telegram = channel === "telegram";

  const [requiresRoll, setRequiresRoll] = useState<boolean | null>(null);

  const read = useCallback(async () => {
    if (!connected || !enabled || !telegram) return;
    try {
      const d = await getChannelDetails("telegram");
      // `?? null` is load-bearing: an OMITTED field is unknown, not `false`.
      setRequiresRoll(d.channelId === "telegram" ? (d.requiresRoll ?? null) : null);
    } catch {
      // Keep the prior verdict, exactly as `useChannelEndpoints` does: a
      // transient read failure is not a state change, and this state moves
      // only on an activation or a roll.
    }
  }, [connected, enabled, telegram]);

  useEffect(() => {
    void read();
  }, [read]);

  useEffect(() => {
    if (!enabled || !connected || !telegram) return;
    const t = setInterval(() => void read(), pollMs);
    return () => clearInterval(t);
  }, [enabled, connected, telegram, pollMs, read]);

  return { requiresRoll, refresh: () => void read() };
}
